<p-toast></p-toast>

<div class="lock-permissions-page">
  <div class="page-header">
    <div class="page-title">
      <img src="assets/icons/main/light/users-main-icon.svg" alt=""/>
      <h2>{{ 'Lock Permissions' | translate }}</h2>
    </div>
    <div class="page-subtitle">
      <p>{{ 'Manage user access restrictions and permissions' | translate }}</p>
    </div>
  </div>

  @if (loading) {
    <div class="h-full w-full">
      <app-loading [loading]="loading"></app-loading>
    </div>
  } @else {
    <div class="page-content">
      <msf-list-page-wrapper
        [leftSize]="'9'"
        [rightSize]="'3'"
        [rightBackground]="false"
      >
        <div left #left>
          <div class="content-wrapper">
            <div class="main-content">
              <div class="management-header">
                <div class="management-title">
                </div>
                <div class="add-button-wrapper">
                  <button pButton type="button" [label]="'Add Lock Permission' | translate" icon="pi pi-plus"
                          class="p-button-rounded add-button" (click)="addLockPermission()">
                  </button>
                </div>
              </div>

              <div class="table-section">
                <p-table
                  #lockPermissionTable
                  [value]="getGroupedDataForTable()"
                  [paginator]="true"
                  [lazy]="true"
                  [rows]="pageSize"
                  [rowsPerPageOptions]="[10, 25, 50, 100]"
                  styleClass="p-datatable-sm user-table"
                  [totalRecords]="totalLockPermissionRecords"
                  paginatorStyleClass="custom-paginator"
                  (onPage)="onPageChange($event)">

                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ 'User' | translate }}</th>
                      <th>{{ 'Blocked Entities Count' | translate }}</th>
                      <th>{{ 'Blocked Actions Count' | translate }}</th>
                      <th style="width: 100px">{{ 'Actions' | translate }}</th>
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="body" let-userGroup>
                    <tr>
                      <td>
                        <div class="user-info">
                          <span class="username">{{ userGroup.user.fullName || userGroup.user.userName }}</span>
                          <span class="user-email">{{ userGroup.user.email }}</span>
                        </div>
                      </td>
                      <td>
                        <span class="count-badge entity-count">
                          {{ getBlockedEntitiesCount(userGroup.permissions) }}
                        </span>
                      </td>
                      <td>
                        <span class="count-badge action-count">
                          {{ getBlockedActionsCount(userGroup.permissions) }}
                        </span>
                      </td>
                      <td>
                        <div class="action-buttons">
                          <button pButton type="button" icon="pi pi-pencil" class="p-button-text p-button-sm"
                                  (click)="editLockPermission(userGroup.user)" 
                                  pTooltip="{{ 'Edit Lock Permissions' | translate }}">
                          </button>
                        </div>
                      </td>
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="paginatorleft">
                    <div class="paginator-left-content">
                      <span class="total-records">{{ 'Total' | translate }}: {{ totalLockPermissionRecords }}</span>
                      <p-select
                        [options]="pageSizeOptions"
                        [(ngModel)]="pageSize"
                        (onChange)="onPageSizeChange($event)"
                        styleClass="page-size-dropdown"
                        appendTo="body"
                        [showClear]="false"
                        optionLabel="label"
                        optionValue="value">
                      </p-select>
                    </div>
                  </ng-template>
                </p-table>
              </div>
            </div>
          </div>
        </div>
        <div right #right>
          <div class="filters-sidebar">
            <msf-filters
              [background]="false"
              [padding]="false"
              [fields]="lockPermissionFilters"
              [(ngModel)]="lockPermissionFiltersData"
              (applyFilter)="applyLockPermissionFilters()"
              (clearFilter)="clearLockPermissionFilters()"
            />
          </div>
        </div>
      </msf-list-page-wrapper>
    </div>
  }
</div>

<!-- Multi-step Lock Permission Dialog -->
<p-dialog [resizable]="false" [draggable]="false" [(visible)]="showLockPermissionDialog"
          [header]="(isEditMode ? 'Edit Lock Permission' : 'Add Lock Permission') | translate"
          [modal]="true" [style]="{width: '900px'}" [dismissableMask]="true" styleClass="lock-permission-dialog">
  
  <!-- Step 1: User Selection -->
  <div *ngIf="currentStep === 1" class="step-content">
    <div class="step-header">
      <h3>{{ 'Select User' | translate }}</h3>
      <p class="step-description">{{ 'Choose the user for whom you want to configure lock permissions' | translate }}</p>
    </div>
    
    <div class="form-group">
      <label>{{ 'User' | translate }} </label>
      <msf-select-paginated
        [(model)]="selectedUser"
        [service]="userService"
        serviceMethod="identityUserGetByQueryPost"
        [placeholder]="'Select user' | translate"
        [isUser]="true"
        optionValue="id"
        searchBy="userName"
        (selectItem)="onUserSelected($event)"
      />
    </div>
  </div>

  <!-- Step 2: Entity and Action Selection -->
  <div *ngIf="currentStep === 2" class="step-content">
    <div *ngIf="isLoadingLockPermission" class="loading-spinner">
      <p-progressSpinner></p-progressSpinner>
    </div>
    
    <div [ngStyle]="{'opacity': isLoadingLockPermission ? 0.5 : 1, 'pointer-events': isLoadingLockPermission ? 'none' : 'auto'}">
      <div class="step-header">
        <h3>{{ (isEditMode ? 'Edit Lock Permissions for' : 'Configure Lock Permissions for') | translate }} {{ selectedUserLabel }}</h3>
        <p class="step-description">{{ 'Select which entities and actions should be blocked for this user' | translate }}</p>
      </div>
      
      <div class="entities-list">
        <div *ngFor="let entityKey of getEntityKeys()" class="entity-row">
          <div class="entity-main">
            <div class="entity-info">
              <h4>{{ getEntityDisplayName(entityKey) }}</h4>
              <div class="entity-indicators">
                <span *ngIf="entityActionSelections[entityKey]?.entityBlocked" class="indicator-block entity-block">
                  <i class="pi pi-ban"></i>
                  {{ 'Entity Blocked' | translate }}
                </span>
                <span *ngIf="entityActionSelections[entityKey]?.instanceBlocked" class="indicator-block instance-block">
                  <i class="pi pi-ban"></i>
                  {{ 'Instance Blocked' | translate }}
                </span>
                <span *ngIf="hasActionBlocks(entityKey)" class="indicator-block action-block">
                  <i class="pi pi-ban"></i>
                  {{ 'Actions Blocked' | translate }}
                </span>
                <span *ngIf="!hasAnyBlocks(entityKey)" class="indicator-none">
                  <i class="pi pi-check"></i>
                  {{ 'No Blocks' | translate }}
                </span>
              </div>
            </div>
            
            <div class="entity-actions">
              <button pButton type="button" 
                      [icon]="entityActionSelections[entityKey]?.expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                      class="p-button-text p-button-sm expand-button"
                      (click)="toggleEntityExpanded(entityKey)"
                      pTooltip="{{ (entityActionSelections[entityKey]?.expanded ? 'Collapse' : 'Expand') | translate }}">
              </button>
            </div>
          </div>
          
          <!-- Expanded Content -->
          <div *ngIf="entityActionSelections[entityKey]?.expanded" class="entity-expanded-content">
            <div class="permission-controls">
              <!-- Permission Type Selection -->
              <div class="control-group">
                <label>{{ 'Permission Type' | translate }}:</label>
                <msf-select
                  [(model)]="entityActionSelections[entityKey].permissionType"
                  [options]="getPermissionTypeOptions()"
                  [placeholder]="'Select permission type' | translate"
                  optionLabel="label"
                  optionValue="value"
                  translateBy="none"
                  (ngModelChange)="setEntityPermissionType(entityKey, $event)"
                  styleClass="permission-type-select"
                />
              </div>
              
              <!-- Entity Block Checkbox (only for Entity type) -->
              <div *ngIf="entityActionSelections[entityKey]?.permissionType === 1" class="control-group">
                <app-checkbox 
                  [(ngModel)]="entityActionSelections[entityKey].entityBlocked"
                  (ngModelChange)="onEntityBlockChange(entityKey, $event)"
                  [label]="'Block entire entity' | translate">
                </app-checkbox>
              </div>
              
              <!-- Instance Block Checkbox (only for Instance type) -->
              <div *ngIf="entityActionSelections[entityKey]?.permissionType === 2" class="control-group">
                <app-checkbox 
                  [(ngModel)]="entityActionSelections[entityKey].instanceBlocked"
                  (ngModelChange)="onInstanceBlockChange(entityKey, $event)"
                  [label]="'Block specific instances' | translate">
                </app-checkbox>
              </div>
            </div>
            
            <!-- Actions Section (only for Action type) -->
            <div *ngIf="entityActionSelections[entityKey]?.permissionType === 0" class="actions-section">
              <h5>{{ 'Select Actions to Block' | translate }}:</h5>
              <div class="actions-grid">
                <div *ngFor="let action of systemMetadata.actions" class="action-option">
                  <app-checkbox 
                    [(ngModel)]="entityActionSelections[entityKey].actions[action.name]"
                    (ngModelChange)="onActionBlockChange(entityKey, action.name, $event)">
                  </app-checkbox>
                  <label>{{ action.displayName }}</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <ng-template pTemplate="footer">
      <div class="footer-left">
        <button
          pButton
          type="button"
          [label]="'Cancel' | translate"
          class="p-button p-button-text"
          (click)="cancelLockPermissionDialog()">
        </button>
      </div>
  
      <div class="footer-right">
        <button
          *ngIf="currentStep > 1"
          pButton
          type="button"
          [label]="'Previous' | translate"
          class="p-button p-button-secondary"
          (click)="goToPreviousStep()">
        </button>
  
        <button
          *ngIf="currentStep === 1"
          pButton
          type="button"
          [label]="'Next' | translate"
          class="p-button p-button-primary"
          [disabled]="!selectedUser"
          (click)="goToNextStep()">
        </button>
  
        <button
          *ngIf="currentStep === 2"
          pButton
          type="button"
          [label]="'Save' | translate"
          class="p-button p-button-success"
          [loading]="isSavingLockPermission"
          (click)="saveMultiStepLockPermission()">
        </button>
      </div>
  </ng-template>
  
</p-dialog>

<!-- Delete Confirmation Dialog -->
<p-dialog [resizable]="false" [draggable]="false" [(visible)]="showDeleteConfirmDialog"
          [header]="'Delete Lock Permission' | translate"
          [modal]="true" [style]="{width: '400px'}" [dismissableMask]="true" styleClass="delete-confirm-dialog">
  <div class="delete-confirm-content">
    <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: #f56565; margin-right: 1rem;"></i>
    <div>
      <p>{{ 'Are you sure you want to delete this lock permission?' | translate }}</p>
      <p class="delete-details">
        <strong>{{ 'User' | translate }}:</strong> {{ getUserName(lockPermissionToDelete?.userId || '') }}<br>
        <strong>{{ 'Entity Type' | translate }}:</strong> {{ lockPermissionToDelete?.entityTypeName }}<br>
        <strong>{{ 'Action' | translate }}:</strong> {{ lockPermissionToDelete?.entityAction || '-' }}
      </p>
    </div>
  </div>
  <ng-template pTemplate="footer">
    <button pButton type="button" [label]="'Cancel' | translate" class="p-button-text"
            (click)="cancelDeleteLockPermission()"></button>
    <button pButton type="button" [label]="'Delete' | translate" class="p-button-danger"
            icon="{{isDeletingLockPermission?'pi pi-spinner pi-spin':''}}"
            [disabled]="isDeletingLockPermission"
            (click)="confirmDeleteLockPermission()"></button>
  </ng-template>
</p-dialog>
